import { useState } from 'react';

type StepDirection = 'next' | 'back' | 'jump';

interface StepChangeEvent<TStep> {
  fromStep: TStep;
  toStep: TStep;
  direction: StepDirection;
  // Time spent on the step being left, in ms. Undefined when there's no
  // recorded timestamp for it (e.g. the very first navigation before any
  // step has been visited long enough to have one).
  timeSpentMs?: number;
}

interface StepNavigationConfig<TStep extends string> {
  initialStep?: number;
  stepEnumOrder: TStep[];
  // Called on every goToStep/goToNextStep/goToPreviousStep navigation (but
  // not setStepSilently) with timing/direction details, for callers that
  // want to track step-navigation analytics.
  onStepChange?: (event: StepChangeEvent<TStep>) => void;
}

const useStepNavigation = <TStep extends string>(
  config: StepNavigationConfig<TStep>
) => {
  const { initialStep = 0, stepEnumOrder, onStepChange } = config;

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [stepTimestamps, setStepTimestamps] = useState<number[]>([Date.now()]);

  const currentStepEnum = stepEnumOrder[currentStep];

  const goToStep = (index: number, direction: StepDirection = 'jump') => {
    const now = Date.now();

    if (onStepChange) {
      onStepChange({
        fromStep: stepEnumOrder[currentStep],
        toStep: stepEnumOrder[index],
        direction,
        timeSpentMs:
          stepTimestamps.length > currentStep
            ? now - stepTimestamps[currentStep]
            : undefined,
      });
    }

    setStepTimestamps(prev => {
      const copy = [...prev];
      copy[index] = now;
      return copy;
    });
    setCurrentStep(index);
  };

  const goToNextStep = () => {
    goToStep(currentStep + 1, 'next');
  };

  const goToPreviousStep = () => {
    goToStep(currentStep - 1, 'back');
  };

  // Moves to a step without notifying onStepChange or crediting time spent
  // on the previous one — for restoring navigation state (e.g. from a URL
  // query param on mount) rather than a user-driven navigation action.
  const setStepSilently = (index: number) => {
    setStepTimestamps(prev => {
      const copy = [...prev];
      while (copy.length <= index) {
        copy.push(Date.now());
      }
      copy[index] = Date.now();
      return copy;
    });
    setCurrentStep(index);
  };

  return {
    currentStep,
    currentStepEnum,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    setStepSilently,
  };
};

export default useStepNavigation;
